import React, { useMemo, useState, useEffect } from "react"

import { Container, CardBody, Card, Modal, ModalBody } from "reactstrap"
import Breadcrumb from "components/Common/Breadcrumb"
import { useNavigate } from "react-router-dom"
import { axiosbaseurl } from "configure"
import moment from "moment"
import Loader from "components/Common/Loader"

import { DatePicker } from "antd"
import { usePermissionData } from "AppContext"
import PermittedComponent from "components/Common/PermittedComponent"
import CommonTable from "components/Common/CommonTable"

const { RangePicker } = DatePicker

const ActivityLogs = () => {
  const [table_data, settable_data] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    getLogsData(
      moment().subtract(30, "days").format("YYYY-MM-DD"),
      moment().format("YYYY-MM-DD")
    )
  }, [])

  const getLogsData = async (startDate, endDate) => {
    try {
      setLoading(true)
      const response = await axiosbaseurl.get(
        `/activity-logs?startDate=${startDate}&endDate=${endDate}`
      )
      if (response.data.success) {
        settable_data(response.data.data)
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }
  }

  document.title = "Audit Logs | Peacock Accountants Limited"

  return (
    <>
      {loading && <Loader />}
      <div className="page-content mb-5">
        <Container fluid>
          <Breadcrumb title={"Dashboard"} breadcrumbItem={"Audit Logs"} />

          <Card className="px-0 mx-0">
            <CardBody>
              <TableComponent
                table_data={table_data}
                getLogsData={getLogsData}
              />
            </CardBody>
          </Card>
        </Container>
      </div>
    </>
  )
}

const TableComponent = ({ table_data, getLogsData }) => {
  const [show, setShow] = useState(false)
  // State variables
  const [dateRange, setDateRange] = useState(["", ""])
  const [activityDetails, setActivityDetails] = useState("")

  const { checkAccess } = usePermissionData()
  const module_id = 25

  const navigate = useNavigate()

  // columns
  const columns = useMemo(
    () => [
      {
        Header: "ID",
        accessor: "id",
        filterable: true,
        disableFilters: true,
        className: "text-center",
        Cell: cellProps => <span>{cellProps.row.index + 1}</span>,
      },
      {
        Header: "Client Code",
        accessor: "client.clientCode",
        disableFilters: true,
        filterable: true,
        className: "text-center",
        Cell: cellProps => (
          <span>
            {cellProps.row.original.client
              ? cellProps.row.original.client.clientCode
              : "--"}
          </span>
        ),
      },
      {
        Header: "Action Title",
        accessor: "action",
        disableFilters: true,
        filterable: true,
        className: "col-3",
      },
      {
        Header: "Previous Data",
        accessor: "previousData",
        disableFilters: true,
        filterable: true,
        className: "d-none",
      },
      {
        Header: "Updated Data",
        accessor: "updatedData",
        disableFilters: true,
        filterable: true,
        className: "d-none",
      },
      {
        Header: "Description",
        accessor: "description",
        disableFilters: true,
        filterable: true,
      },
      {
        Header: "Action Performed At",
        accessor: "createdAt",
        disableFilters: true,
        filterable: true,
        className: "col-3",
        Cell: cellProps => {
          return (
            <span>
              {moment(cellProps.row.original.createdAt).format(
                "DD MMM YYYY | hh:mm A"
              )}
            </span>
          )
        },
      },
      {
        Header: "Action",
        disableFilters: true,
        disableSort: true,
        accessor: "view",
        className: "col-1 text-center",
        Cell: cellProps => {
          return (
            <div>
              <button
                onClick={() => {
                  setShow(true)
                  setActivityDetails(cellProps.row.original)
                }}
                className="btn btn-primary btn-sm me-2 "
              >
                <i className="fas fa-eye me-1"></i> View
              </button>
            </div>
          )
        },
      },
    ],
    []
  )

  // table data
  const data = useMemo(() => table_data, [table_data])

  // Filter records based on the date range
  const handleFilter = ([start, end]) => {
    if (start && end) {
      getLogsData(start, end)
    } else {
      getLogsData(
        moment().subtract(30, "days").format("YYYY-MM-DD"),
        moment().format("YYYY-MM-DD")
      )
    }
  }

  const handleReset = () => {
    if (dateRange[0] && dateRange[1]) {
      getLogsData(
        moment().subtract(30, "days").format("YYYY-MM-DD"),
        moment().format("YYYY-MM-DD")
      )
      setDateRange(["", ""])
    }
  }

  return (
    <>
      <PermittedComponent isAccess={checkAccess(module_id, "view")}>
        <CommonTable
          columns={columns}
          data={data}
          heading={
            <div className="d-flex align-items-center">
              <div>
                <RangePicker
                  onChange={(_, dateString) => {
                    setDateRange(dateString)
                    handleFilter(dateString)
                  }}
                  value={
                    dateRange[0] && dateRange[1]
                      ? [moment(dateRange[0]), moment(dateRange[1])]
                      : null
                  }
                  className="py-2 rounded"
                />
              </div>

              <div>
                <button
                  type="button"
                  onClick={handleReset}
                  className="btn btn-primary ms-2"
                  disabled={dateRange.includes("")}
                >
                  <i className="fas fa-undo me-2"></i> Reset
                </button>
              </div>
            </div>
          }
        >
          <div>
            <button
              onClick={() => navigate(-1)}
              className="btn btn-secondary bg-dark ms-3"
            >
              <i className="fas fa-arrow-left me-3"></i>
              Back
            </button>
          </div>
        </CommonTable>

        <Modal
          size="xl"
          isOpen={show}
          toggle={() => setShow(!show)}
          centered={true}
        >
          <div className="modal-content">
            <ModalBody className="px-3">
              <div className="d-flex justify-content-between">
                <h5>Activity Performed</h5>
                <button
                  type="button"
                  className="btn-close "
                  onClick={() => setShow(!show)}
                ></button>
              </div>

              <table className="table mt-4">
                <tbody>
                  <tr>
                    <th className="table-primary col-2">Date & Time</th>
                    <td>
                      {" "}
                      {moment(activityDetails?.createdAt).format(
                        "DD MMM YYYY | hh:mm A"
                      )}{" "}
                    </td>
                  </tr>
                  <tr>
                    <th className="table-primary">Activity</th>
                    <td> {activityDetails?.description} </td>
                  </tr>
                  {activityDetails?.createdData && (
                    <tr>
                      <th className="table-primary col-2">Created Data</th>
                      <td>
                        <table border="1" className="table">
                          <tbody className="table-light">
                            {Object.entries(
                              JSON.parse(activityDetails?.createdData)
                            ).map(([key, value]) => (
                              <tr key={key}>
                                <th className="py-2 ">
                                  {key
                                    .replace(/([A-Z])/g, " $1")
                                    .replace(/^./, str => str.toUpperCase())}
                                </th>
                                <td className="py-2">
                                  {moment(value).format("DD MMMM, YYYY ") !==
                                    "Invalid date" &&
                                  value.length > 8 &&
                                  value.includes("-") ? (
                                    moment(value).format("DD MMMM, YYYY ")
                                  ) : (
                                    <div
                                      dangerouslySetInnerHTML={{
                                        __html: value || "--",
                                      }}
                                      style={{ textWrap: "wrap" }}
                                    />
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                  {activityDetails?.previousData && (
                    <tr>
                      <th className="table-primary col-2">Previous Data</th>

                      <td>
                        <table border="1" className="table">
                          <tbody className="table-light">
                            {Object.entries(
                              JSON.parse(activityDetails?.previousData)
                            ).map(([key, value]) => (
                              <tr key={key}>
                                <th className="py-2 col-3 ">
                                  {key
                                    .replace(/([A-Z])/g, " $1")
                                    .replace(/^./, str => str.toUpperCase())}
                                </th>
                                <td className="py-2">
                                  {moment(value).format("DD MMMM, YYYY ") !==
                                    "Invalid date" &&
                                  value.length > 8 &&
                                  value.includes("-") ? (
                                    moment(value).format("DD MMMM, YYYY ")
                                  ) : (
                                    <div
                                      dangerouslySetInnerHTML={{
                                        __html: value || "--",
                                      }}
                                      style={{ textWrap: "wrap" }}
                                    />
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                  {activityDetails?.updatedData && (
                    <tr>
                      <th className="table-primary col-2">Updated Data</th>
                      <td>
                        <table border="1" className="table">
                          <tbody className="table-light">
                            {Object.entries(
                              JSON.parse(activityDetails?.updatedData)
                            ).map(([key, value]) => (
                              <tr key={key}>
                                <th className="py-2 col-3">
                                  {key
                                    .replace(/([A-Z])/g, " $1")
                                    .replace(/^./, str => str.toUpperCase())}
                                </th>
                                <td className="py-2">
                                  {moment(value).format("DD MMMM, YYYY ") !==
                                    "Invalid date" &&
                                  value.length > 8 &&
                                  value.includes("-") ? (
                                    moment(value).format("DD MMMM, YYYY ")
                                  ) : (
                                    <div
                                      dangerouslySetInnerHTML={{
                                        __html: value || "--",
                                      }}
                                      style={{ textWrap: "wrap" }}
                                    />
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                  {activityDetails?.deletedData && (
                    <tr>
                      <th className="table-primary col-2">Deleted Data</th>
                      <td>
                        <table border="1" className="table">
                          <tbody className="table-light">
                            {Object.entries(
                              JSON.parse(activityDetails?.deletedData)
                            ).map(([key, value]) => (
                              <tr key={key}>
                                <th className="py-2 ">
                                  {key
                                    .replace(/([A-Z])/g, " $1")
                                    .replace(/^./, str => str.toUpperCase())}
                                </th>
                                <td className="py-2">
                                  {moment(value).format("DD MMMM, YYYY ") !==
                                    "Invalid date" &&
                                  value.length > 8 &&
                                  value.includes("-") ? (
                                    moment(value).format("DD MMMM, YYYY ")
                                  ) : (
                                    <div
                                      dangerouslySetInnerHTML={{
                                        __html: value || "--",
                                      }}
                                      style={{ textWrap: "wrap" }}
                                    />
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </ModalBody>
          </div>
        </Modal>
      </PermittedComponent>
    </>
  )
}

export default ActivityLogs
